import {Song,Synth} from "../synth/synth.js";
import {measureAudio,audioReportMetadata} from "./AudioReport.js";
import {songToMidi} from "./MidiExport.js";
// Copyright (c) John Nesky and contributing authors, distributed under the MIT license, see accompanying the LICENSE.md file.

import {ColorConfig} from "./ColorConfig.js";
import {SongDocument} from "./SongDocument.js";
import {Prompt} from "./Prompt.js";
import {HTML} from "imperative-html/dist/esm/elements-strict.js";
import {SongRenderer} from "./SongRenderer.js";

const {button, div, h2, input, progress, select, option} = HTML;

function save(blob: Blob, name: string): void {
	if ((<any>navigator).msSaveOrOpenBlob) {
		(<any>navigator).msSaveOrOpenBlob(blob, name);
		return;
	}
	
	const anchor: HTMLAnchorElement = document.createElement("a");
	if (anchor.download != undefined) {
		const url: string = URL.createObjectURL(blob);
		setTimeout(function() { URL.revokeObjectURL(url); }, 60000);
		anchor.href = url;
		anchor.download = name;
		// Chrome bug regression: We need to delay dispatching the click
		// event. Seems to be related to going back in the browser history.
		// https://bugs.chromium.org/p/chromium/issues/detail?id=825100
		setTimeout(function() { anchor.dispatchEvent(new MouseEvent("click")); }, 0);
	} else {
		const url: string = URL.createObjectURL(blob);
		setTimeout(function() { URL.revokeObjectURL(url); }, 60000);
		if (!window.open(url, "_blank")) window.location.href = url;
	}
}

export class ExportPrompt implements Prompt {
	private readonly _fileName: HTMLInputElement = input({type: "text", style: "width: 10em;", value: "Bop-Song", maxlength: 250, "autofocus": "autofocus"});
	private readonly _enableIntro: HTMLInputElement = input({type: "checkbox"});
	private readonly _loopDropDown: HTMLInputElement = input({style:"width: 2em;", type: "number", min: "1", max: "4", step: "1"});
	private readonly _enableOutro: HTMLInputElement = input({type: "checkbox"});
	private readonly _formatSelect: HTMLSelectElement = select({style: "width: 100%;", "aria-label":"Export format"},
		option({value: "wav"}, ".wav"),
		option({value: "mp3"}, ".mp3"),
		option({value: "midi"}, ".mid"),
		option({value: "json"}, ".json (editable song)"),
		option({value: "report"}, ".json (audio report for agents)"),
		option({value: "html"}, ".html (opens Bop)"),
	);
	private readonly _trackSelect: HTMLSelectElement = select({style:"width:100%", "aria-label":"Audio track"},option({value:"mix"},"Full mix"));
    private readonly _trackRow = div({},"Audio track:",this._trackSelect);
    private readonly _renderHelp = div({style:"font-size:12px;line-height:1.4;margin:8px 0;"},"Choose one track for an aligned WAV stem. Audio reports measure the mix and every track locally; no audio is uploaded.");
    private readonly _reportCopy = button({style:"display:none;"},"Copy report for agent");
    private _reportText="";
    private readonly _progressBar: HTMLProgressElement = progress({style: "display: none;", max: "1"});
	private readonly _statusMessage: HTMLDivElement = div({style: "display: none; text-align: center;"});
	private readonly _cancelButton: HTMLButtonElement = button({class: "cancelButton"});
	private readonly _exportButton: HTMLButtonElement = button({class: "exportButton", style: "width:45%;"}, "Export");
	private readonly _downloadButton: HTMLButtonElement = button({class: "exportButton", style: "width:45%; display: none;"}, "Save File");

	private _canceled: boolean = false;
	
	public readonly container: HTMLDivElement = div({class: "prompt noSelection", style: "width: 200px;"},
		h2("Export Options"),
		div({style: "display: flex; flex-direction: row; align-items: center; justify-content: space-between;"},
			"File name:",
			this._fileName,
		),
		div({style: "display: table; width: 100%;"},
			div({style: "display: table-row;"},
				div({style: "display: table-cell;"}, "Intro:"),
				div({style: "display: table-cell;"}, "Loop Count:"),
				div({style: "display: table-cell;"}, "Outro:"),
			),
			div({style: "display: table-row;"},
				div({style: "display: table-cell; vertical-align: middle;"}, this._enableIntro),
				div({style: "display: table-cell; vertical-align: middle;"}, this._loopDropDown),
				div({style: "display: table-cell; vertical-align: middle;"}, this._enableOutro),
			),
		),
		div({style: "text-align: left;"}, "File Type:"),
		div({class: "selectContainer", style: "width: 100%;"}, this._formatSelect),
		this._trackRow,
        this._renderHelp,
        this._reportCopy,
		this._progressBar,
		this._statusMessage,
		div({style: "display: flex; flex-direction: row-reverse; justify-content: space-between;"},
			this._exportButton,
			this._downloadButton,
		),
		this._cancelButton,
	);
	
	constructor(private _doc: SongDocument) {
		this._loopDropDown.value = "1";
        this._doc.song.channels.forEach((channel,i)=>this._trackSelect.add(option({value:String(i)},`${i+1}. ${channel.name||"Track "+(i+1)}`)));
        this._formatSelect.addEventListener("change",this._formatChanged);
        this._reportCopy.addEventListener("click",this._copyReport);
		
		if (this._doc.song.loopStart == 0) {
			this._enableIntro.checked = false;
			this._enableIntro.disabled = true;
		} else {
			this._enableIntro.checked = true;
			this._enableIntro.disabled = false;
		}
		if (this._doc.song.loopStart + this._doc.song.loopLength == this._doc.song.barCount) {
			this._enableOutro.checked = false;
			this._enableOutro.disabled = true;
		} else {
			this._enableOutro.checked = true;
			this._enableOutro.disabled = false;
		}
		
		const lastExportFormat: string | null = window.localStorage.getItem("exportFormat");
		if (lastExportFormat != null) {
			this._formatSelect.value = lastExportFormat;
		}
		
		this._formatChanged();
		this._fileName.select();
		setTimeout(()=>this._fileName.focus());
		
		this._fileName.addEventListener("input", ExportPrompt._validateFileName);
		this._loopDropDown.addEventListener("blur", ExportPrompt._validateNumber);
		this._exportButton.addEventListener("click", this._export);
		this._cancelButton.addEventListener("click", this._close);
		this.container.addEventListener("keydown", this._whenKeyPressed);
	}
	
	private _close = (): void => { 
		this._canceled = true;
		this._doc.undo();
	}
	
	public cleanUp = (): void => { 
		this._canceled = true;
		this._formatSelect.removeEventListener("change",this._formatChanged);
        this._reportCopy.removeEventListener("click",this._copyReport);
		this._fileName.removeEventListener("input", ExportPrompt._validateFileName);
		this._loopDropDown.removeEventListener("blur", ExportPrompt._validateNumber);
		this._exportButton.removeEventListener("click", this._export);
		this._cancelButton.removeEventListener("click", this._close);
		this.container.removeEventListener("keydown", this._whenKeyPressed);
	}
	
	private _whenKeyPressed = (event: KeyboardEvent): void => {
		if ((<Element> event.target).tagName != "BUTTON" && event.keyCode == 13) { // Enter key
			this._export();
		}
	}
	
	private static _validateFileName(event: Event): void {
		const input: HTMLInputElement = <HTMLInputElement>event.target;
		const deleteChars = /[\+\*\$\?\|\{\}\\\/<>#%!`&'"=:@]/gi;
		if (deleteChars.test(input.value)) {
			let cursorPos: number = <number>input.selectionStart;
			input.value = input.value.replace(deleteChars, "");
			cursorPos--;
			input.setSelectionRange(cursorPos, cursorPos);
		}
	}
	
	private static _validateNumber(event: Event): void {
		const input: HTMLInputElement = <HTMLInputElement>event.target;
		input.value = Math.floor(Math.max(Number(input.min), Math.min(Number(input.max), Number(input.value)))) + "";
	}
	
	private _formatChanged = ():void => {
        const audio=["wav","mp3"].includes(this._formatSelect.value);
        this._trackRow.style.display=audio?"block":"none";
        this._renderHelp.style.display=audio||this._formatSelect.value==="report"?"block":"none";
    }
    private _copyReport = async():Promise<void> => {
        try{await navigator.clipboard.writeText(this._reportText);this._reportCopy.textContent="Copied";}
        catch{this._statusMessage.textContent="Clipboard unavailable. Save the JSON report and share its contents.";}
    }
    private _export = (): void => {
		window.localStorage.setItem("exportFormat", this._formatSelect.value);
		this._exportButton.style.display = "none";
		this._enableIntro.disabled = true;
		this._loopDropDown.disabled = true;
		this._enableOutro.disabled = true;
		this._formatSelect.disabled = true;
        this._trackSelect.disabled=true;
		switch(this._formatSelect.value) {
			case "report":
                this._exportReport().catch(error=>{if(!this._canceled){this._statusMessage.style.display="block";this._statusMessage.textContent=error.message;}});
                break;
			case "wav":
				this._exportToWav();
				break;
			case "mp3":
				this._exportToMp3();
				break;
			case "midi":
				this._exportToMidi();
				break;
			case "json":
				this._exportToJson();
				break;
			case "html":
				this._exportToHtml();
				break;
			default:
				throw new Error("Unhandled file export type.");
		}
	}
	
    private _audioFileName():string {
        return this._fileName.value.trim()+(this._trackSelect.value==="mix"?"":`-track-${Number(this._trackSelect.value)+1}`);
    }
    private _audioSong(track=this._trackSelect.value):Song {
        const copy=new Song(this._doc.song.toBase64String());
        // Full mix follows editor mutes; selecting one stem explicitly isolates it.
        copy.channels.forEach((c,i)=>c.muted=track==="mix"?this._doc.song.channels[i].muted:i!==Number(track));
        return copy;
    }
    private async _exportReport():Promise<void> {
        const rate=48000,song=this._audioSong("mix"),hash=song.toBase64String(),order:number[]=[];
        if(this._enableIntro.checked)for(let i=0;i<song.loopStart;i++)order.push(i+1);
        for(let repeat=0;repeat<Number(this._loopDropDown.value);repeat++)for(let i=song.loopStart;i<song.loopStart+song.loopLength;i++)order.push(i+1);
        if(this._enableOutro.checked)for(let i=song.loopStart+song.loopLength;i<song.barCount;i++)order.push(i+1);
        const probe=new Synth(song);probe.samplesPerSecond=rate;const samplesPerBar=probe.getSamplesPerBar();
        if(samplesPerBar*order.length*8>256*1024*1024)throw new Error("This render exceeds the browser memory budget. Reduce loop count or song length.");
        const digest=await crypto.subtle.digest("SHA-256",new TextEncoder().encode(hash));
        const fingerprint=Array.from(new Uint8Array(digest),b=>b.toString(16).padStart(2,"0")).join("");
        const passes:any[]=[];this._progressBar.style.display=this._statusMessage.style.display="block";
        for(let track=-1;track<song.channels.length;track++){
            this._statusMessage.textContent=track<0?"Measuring full mix…":`Measuring track ${track+1} of ${song.channels.length}…`;
            const renderer=new SongRenderer();
            for await(const progress of renderer.generate(this._audioSong(track<0?"mix":String(track)),rate,this._enableIntro.checked,this._enableOutro.checked,Number(this._loopDropDown.value))){
                renderer.canceled=this._canceled;if(this._canceled)return;
                this._progressBar.value=(track+1+progress)/(song.channels.length+1);
            }
            const l=renderer.outputSamplesL,r=renderer.outputSamplesR;
            passes.push({track:track<0?"mix":track+1,name:track<0?"Full mix":song.channels[track].name||`Track ${track+1}`,mutedInMix:track>=0&&song.channels[track].muted,summary:measureAudio(l,r),bars:order.map((bar,i)=>{const m=measureAudio(l,r,Math.min(l.length,Math.ceil(i*samplesPerBar)),Math.min(l.length,Math.ceil((i+1)*samplesPerBar)));return [bar,i+1,Math.round(i*samplesPerBar/rate*1000)/1000,m.rmsDbFS,m.samplePeakDbFS,m.clippedSamples];})});
        }
        this._reportText=JSON.stringify({...audioReportMetadata(fingerprint,rate,order),durationSeconds:samplesPerBar*order.length/rate,mutedTracks:song.channels.map((channel,i)=>channel.muted?i+1:0).filter(Boolean),passes});
        const blob=new Blob([this._reportText],{type:"application/json"});
        this._progressBar.style.display="none";this._statusMessage.textContent="Report ready. Share it with your agent alongside your listening feedback.";
        this._downloadButton.style.display=this._reportCopy.style.display="block";
        this._downloadButton.onclick=()=>save(blob,this._fileName.value.trim()+".audio-report.json");
    }
	private async _synthesize(sampleRate: number): Promise<{recordedSamplesL: Float32Array, recordedSamplesR: Float32Array}> {
		this._progressBar.style.display = "block";
		this._statusMessage.style.display = "block";
		this._statusMessage.textContent = "Synthesizing...";
		const renderer: SongRenderer = new SongRenderer();
		for await (const completionRate of renderer.generate(this._audioSong(), sampleRate, this._enableIntro.checked, this._enableOutro.checked, Number(this._loopDropDown.value))) {
			this._progressBar.value = completionRate;
			renderer.canceled = this._canceled;
			if (this._canceled) break;
		}
		this._progressBar.style.display = "none";
		this._statusMessage.style.display = "none";
		return {recordedSamplesL: renderer.outputSamplesL, recordedSamplesR: renderer.outputSamplesR};
	}
	
	private async _exportToWav(): Promise<void> {
		const sampleRate: number = 48000; // Use professional video editing standard sample rate for .wav file export.
		const {recordedSamplesL, recordedSamplesR} = await this._synthesize(sampleRate);
		if (this._canceled) return;
		const sampleFrames: number = recordedSamplesL.length;
		
		const wavChannelCount: number = 2;
		const bytesPerSample: number = 2;
		const bitsPerSample: number = 8 * bytesPerSample;
		const sampleCount: number = wavChannelCount * sampleFrames;
		
		const totalFileSize: number = 44 + sampleCount * bytesPerSample;
		
		let index: number = 0;
		const arrayBuffer: ArrayBuffer = new ArrayBuffer(totalFileSize);
		const data: DataView = new DataView(arrayBuffer);
		data.setUint32(index, 0x52494646, false); index += 4;
		data.setUint32(index, 36 + sampleCount * bytesPerSample, true); index += 4; // size of remaining file
		data.setUint32(index, 0x57415645, false); index += 4;
		data.setUint32(index, 0x666D7420, false); index += 4;
		data.setUint32(index, 0x00000010, true); index += 4; // size of following header
		data.setUint16(index, 0x0001, true); index += 2; // not compressed
		data.setUint16(index, wavChannelCount, true); index += 2; // channel count
		data.setUint32(index, sampleRate, true); index += 4; // sample rate
		data.setUint32(index, sampleRate * bytesPerSample * wavChannelCount, true); index += 4; // bytes per second
		data.setUint16(index, bytesPerSample * wavChannelCount, true); index += 2; // block align
		data.setUint16(index, bitsPerSample, true); index += 2; // bits per sample
		data.setUint32(index, 0x64617461, false); index += 4;
		data.setUint32(index, sampleCount * bytesPerSample, true); index += 4;
		
		if (bytesPerSample > 1) {
			// usually samples are signed. 
			const range: number = (1 << (bitsPerSample - 1)) - 1;
			for (let i: number = 0; i < sampleFrames; i++) {
				let valL: number = Math.floor(Math.max(-1, Math.min(1, recordedSamplesL[i])) * range);
				let valR: number = Math.floor(Math.max(-1, Math.min(1, recordedSamplesR[i])) * range);
				if (bytesPerSample == 2) {
					data.setInt16(index, valL, true); index += 2;
					data.setInt16(index, valR, true); index += 2;
				} else if (bytesPerSample == 4) {
					data.setInt32(index, valL, true); index += 4;
					data.setInt32(index, valR, true); index += 4;
				} else {
					throw new Error("unsupported sample size");
				}
			}
		} else {
			// 8 bit samples are a special case: they are unsigned.
			for (let i: number = 0; i < sampleFrames; i++) {
				let valL: number = Math.floor(Math.max(-1, Math.min(1, recordedSamplesL[i])) * 127 + 128);
				let valR: number = Math.floor(Math.max(-1, Math.min(1, recordedSamplesR[i])) * 127 + 128);
				data.setUint8(index, valL > 255 ? 255 : (valL < 0 ? 0 : valL)); index++;
				data.setUint8(index, valR > 255 ? 255 : (valR < 0 ? 0 : valR)); index++;
			}
		}
		
		const blob: Blob = new Blob([arrayBuffer], {type: "audio/wav"});
		this._downloadButton.style.display = "";
		this._downloadButton.addEventListener("click", () => {
			save(blob, this._audioFileName() + ".wav");
			this._close();
		});
	}
	
	private _exportToMp3(): void {
		const whenEncoderIsAvailable = async (): Promise<void> => {
			const sampleRate: number = 44100; // Use consumer CD standard sample rate for .mp3 export.
			const {recordedSamplesL, recordedSamplesR} = await this._synthesize(sampleRate);
			if (this._canceled) return;
			
			this._progressBar.style.display = "block";
			this._statusMessage.style.display = "block";
			this._statusMessage.textContent = "Compressing...";
			
			const lamejs: any = (<any> window)["lamejs"];
			const channelCount: number = 2;
			const kbps: number = 192;
			const sampleBlockSize: number = 1152;
			const mp3encoder: any = new lamejs.Mp3Encoder(channelCount, sampleRate, kbps);
			const mp3Data: any[] = [];
			
			const left: Int16Array = new Int16Array(recordedSamplesL.length);
			const right: Int16Array = new Int16Array(recordedSamplesR.length);
			const range: number = (1 << 15) - 1;
			for (let i: number = 0; i < recordedSamplesL.length; i++) {
				left[i]  = Math.floor(Math.max(-1, Math.min(1, recordedSamplesL[i])) * range);
				right[i] = Math.floor(Math.max(-1, Math.min(1, recordedSamplesR[i])) * range);
			}
			
			let lastRenderTime: number = performance.now();
			for (let i: number = 0; i < left.length; i += sampleBlockSize) {
				const leftChunk: Int16Array = left.subarray(i, i + sampleBlockSize);
				const rightChunk: Int16Array = right.subarray(i, i + sampleBlockSize);
				const mp3buf: any = mp3encoder.encodeBuffer(leftChunk, rightChunk);
				if (mp3buf.length > 0) mp3Data.push(mp3buf);
				
				const now = performance.now();
				if (now - lastRenderTime > 1000 / 30) {
					lastRenderTime = now;
					this._progressBar.value = i / left.length;
					// Give the browser a chance to render a progress bar.
					await new Promise((resolve) => requestAnimationFrame(resolve));
					if (this._canceled) return;
				}
			}
			const mp3buf: any = mp3encoder.flush();
			if (mp3buf.length > 0) mp3Data.push(mp3buf);
			
			this._progressBar.style.display = "none";
			this._statusMessage.style.display = "none";
			
			const blob: Blob = new Blob(mp3Data, {type: "audio/mp3"});
			this._downloadButton.style.display = "";
			this._downloadButton.addEventListener("click", () => {
				save(blob, this._audioFileName() + ".mp3");
				this._close();
			});
		}
		
		if ("lamejs" in window) {
			whenEncoderIsAvailable();
		} else {
			var script = document.createElement("script");
			script.src = "https://cdn.jsdelivr.net/npm/lamejs@1.2.0/lame.min.js";
			script.onload = whenEncoderIsAvailable;
			document.head.appendChild(script);
		}
	}
	
	private _exportToMidi(): void {
        const bytes = songToMidi(this._doc.song, {
            includeIntro: this._enableIntro.checked, loops: Number(this._loopDropDown.value), includeOutro: this._enableOutro.checked,
            channelLabel: channel => ColorConfig.getChannelColor(this._doc.song, channel).name + " channel",
        });
        save(new Blob([bytes], {type: "audio/midi"}), this._fileName.value.trim() + ".mid");
        this._close();
    }

	private _exportToJson(): void {
		const jsonObject: Object = this._doc.song.toJsonObject(this._enableIntro.checked, Number(this._loopDropDown.value), this._enableOutro.checked);
		const jsonString: string = JSON.stringify(jsonObject, null, '\t');
		const blob: Blob = new Blob([jsonString], {type: "application/json"});
		save(blob, this._fileName.value.trim() + ".json");
		this._close();
	}
	
	private _exportToHtml(): void {
		const fileContents = `\
<!DOCTYPE html><meta charset="utf-8">

You should be redirected to the song at:<br /><br />

<a id="destination" href="${new URL("#" + this._doc.song.toBase64String(), location.protocol == "file:" ? "https://sahildev.com/bop/" : location.href).href}"></a>

<style>
	:root {
		color: white;
		background: black;
		font-family:
		sans-serif;
	}
	a {
		color: #98f;
	}
	a[href]::before {
		content: attr(href);
	}
</style>

<script>
	location.assign(document.querySelector("a#destination").href);
</script>
`;
		const blob: Blob = new Blob([fileContents], {type: "text/html"});
		save(blob, this._fileName.value.trim() + ".html");
		this._close();
	}
}
